import { yupResolver } from '@hookform/resolvers/yup';
import { message } from 'antd';
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';

import {
  type BannerRootData,
  bannerConfigurationSchema,
  bannerContentSchema,
  bannerRootDefaultValues,
  bannerRootValidationSchema,
  bannerScheduleSchema,
} from '../schemas/bannerSchema';
import { BannerStatusEnum } from '../types/bannerManagement';
import { toBannerFormValues } from '../utils/toBannerFormValues';
import {
  useBannerActivate,
  useBannerCreate,
  useBannerItem,
  useBannerUpdate,
} from './useBanner';

const TOTAL_STEPS = 3;

const STEP_FIELDS: Record<number, keyof BannerRootData> = {
  0: 'configuration',
  1: 'content',
  2: 'schedule',
};

export function useBannerForm() {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const isEditing = !!id;
  const bannerId = id ? Number(id) : undefined;
  const [currentStep, setCurrentStep] = useState(0);
  const [visitedSteps, setVisitedSteps] = useState<Set<number>>(new Set());
  const [showUnsavedAlert, setShowUnsavedAlert] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const markStepVisited = useCallback((step: number) => {
    setVisitedSteps(prev => new Set(prev).add(step));
  }, []);

  const { banner: existingBanner, isLoading: isLoadingBanner } =
    useBannerItem(bannerId);

  const bannerFormMethods = useForm<BannerRootData>({
    resolver: yupResolver(bannerRootValidationSchema),
    mode: 'onChange',
    reValidateMode: 'onBlur',
    defaultValues: bannerRootDefaultValues,
  });

  const {
    trigger,
    watch,
    formState: { isDirty, errors },
  } = bannerFormMethods;

  const formValues = watch();

  const handleChangeStep = useCallback(
    (step: number) => {
      markStepVisited(currentStep);
      const fieldKey = STEP_FIELDS[currentStep];
      if (fieldKey) trigger(fieldKey);
      setCurrentStep(step);
    },
    [currentStep, markStepVisited, trigger]
  );

  const { createBanner, isCreatingBanner } = useBannerCreate(bannerFormMethods);
  const { updateBanner, isUpdatingBanner } = useBannerUpdate(
    bannerId ?? 0,
    bannerFormMethods
  );
  const { activateBanner, isActivatingBanner } = useBannerActivate();

  const stepsCompleted = useMemo(() => {
    const validateStep = (schema: any, data: any): boolean => {
      try {
        schema.validateSync(data);
        return true;
      } catch {
        return false;
      }
    };
    return {
      configuration: validateStep(
        bannerConfigurationSchema,
        formValues.configuration
      ),
      content: validateStep(bannerContentSchema, formValues.content),
      schedule: validateStep(bannerScheduleSchema, formValues.schedule),
    };
  }, [formValues]);

  const stepsWithErrors = useMemo(
    () => ({
      configuration: visitedSteps.has(0) && !stepsCompleted.configuration,
      content: visitedSteps.has(1) && !stepsCompleted.content,
      schedule: visitedSteps.has(2) && !stepsCompleted.schedule,
    }),
    [stepsCompleted, visitedSteps]
  );

  const canPublish =
    stepsCompleted.configuration &&
    stepsCompleted.content &&
    stepsCompleted.schedule;

  const bannerStatus = existingBanner?.status ?? null;
  const isReadOnly =
    bannerStatus === BannerStatusEnum.Active ||
    bannerStatus === BannerStatusEnum.Scheduled;

  useEffect(() => {
    if (isDirty) {
      setShowUnsavedAlert(true);
    }
  }, [isDirty]);

  useEffect(() => {
    if (existingBanner) {
      bannerFormMethods.reset(toBannerFormValues(existingBanner));
      setVisitedSteps(new Set([0, 1, 2]));
      trigger();
    }
  }, [existingBanner, bannerFormMethods, trigger]);

  // Core save: calls create or update based on current editing state.
  // Returns the bannerId on success, or false on failure.
  const saveBanner = useCallback(async (): Promise<number | false> => {
    const data = bannerFormMethods.getValues();

    try {
      if (isEditing) {
        await updateBanner({ formData: data });
        bannerFormMethods.reset(data);
        setShowUnsavedAlert(false);
        return bannerId as number;
      } else {
        const { bannerId: createdId } = await createBanner({ formData: data });
        navigate(`/marketplace/banners/create-or-update/${createdId}`, {
          replace: true,
        });
        setShowUnsavedAlert(false);
        return createdId;
      }
    } catch (error) {
      console.error(error);
      return false;
    }
  }, [
    bannerFormMethods,
    isEditing,
    bannerId,
    updateBanner,
    createBanner,
    navigate,
  ]);

  // Draft: skips validation, always saves.
  const handleSaveBannerDraft = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await saveBanner();
      if (result !== false) {
        message.success('Banner salvo com sucesso');
      } else {
        message.error('Erro ao salvar rascunho');
      }
    } finally {
      setIsLoading(false);
    }
  }, [saveBanner]);

  // Next: validates current step fields, saves, then advances (or finishes on last step).
  const handleNext = useCallback(async () => {
    setIsLoading(true);
    try {
      const fieldKey = STEP_FIELDS[currentStep];
      markStepVisited(currentStep);
      const isValid = fieldKey ? await trigger(fieldKey) : true;

      if (!isValid) return;

      const result = await saveBanner();

      if (result === false) {
        message.error('Erro ao salvar banner');
        return;
      }

      const isLastStep = currentStep === TOTAL_STEPS - 1;

      if (isLastStep) {
        const isActive = bannerFormMethods.getValues('schedule.isActive');
        const scheduledDate = bannerFormMethods.getValues(
          'schedule.scheduledDate'
        );
        const isScheduledInPast =
          !scheduledDate || !dayjs(scheduledDate as string).isAfter(dayjs());

        if (isActive && isScheduledInPast) {
          await activateBanner(result);
        }

        message.success('Banner salvo com sucesso');
        navigate('/marketplace/banners/gerenciamento');
      } else {
        setCurrentStep(prev => prev + 1);
      }
    } catch (error) {
      message.error('Erro ao avançar para o próximo passo');
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }, [
    currentStep,
    saveBanner,
    trigger,
    navigate,
    activateBanner,
    bannerFormMethods,
    markStepVisited,
  ]);

  const handleBack = useCallback(() => {
    markStepVisited(currentStep);
    const fieldKey = STEP_FIELDS[currentStep];
    if (fieldKey) trigger(fieldKey);
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    } else {
      navigate(-1);
    }
  }, [currentStep, navigate, markStepVisited, trigger]);

  return {
    bannerId,
    bannerFormMethods,
    currentStep,
    handleChangeStep,
    handleSaveBannerDraft,
    handleNext,
    handleBack,
    showUnsavedAlert,
    setShowUnsavedAlert,
    canPublish,
    stepsCompleted,
    stepsWithErrors,
    bannerStatus,
    isReadOnly,
    errors,
    isLoading:
      isLoading ||
      isCreatingBanner ||
      isUpdatingBanner ||
      isLoadingBanner ||
      isActivatingBanner,
  };
}
